import { useLayoutEffect, useRef } from 'react'
import { DURATION, EASE } from '@/lib/motion'

/**
 * A figure animates updates from its last acknowledged value. Initial reads show their final
 * value immediately; later updates can count between values. The final value is in the markup, so
 * screen readers, tests and no-JS see the truth; the count only rewrites the visible text, through
 * a ref, so React does not re-render per frame. Off under «reduce motion» and in jsdom.
 *
 * ⚠️ It writes `nodeValue` on React's OWN text node, never `textContent`: assigning `textContent`
 * replaces the node, React keeps updating the detached one, and the figure froze on its first value
 * (setup review 25.10.2026: «Gjør klart» said 3 beside a meter showing 4). The cleanup leaves the
 * text alone, because React writes the new value before it runs; the next run restores React's
 * text only if the node still holds a number the count wrote.
 */
export function useCountUp<T extends HTMLElement>(value: number | null | undefined) {
  const ref = useRef<T>(null)
  // The number actually on screen, not the last target: an effect that runs twice (StrictMode)
  // or is interrupted must still start from what the reader sees.
  const displayed = useRef<number | null>(null)
  // What the count last wrote, and React's text it stands in for.
  const written = useRef<{ shown: string; final: string | null } | null>(null)
  // Before paint, so the final number never flashes before the count starts.
  useLayoutEffect(() => {
    const el = ref.current
    if (!el || value == null || !Number.isFinite(value)) return
    const from = displayed.current ?? value
    const reduce = typeof window === 'undefined' || !window.matchMedia
      || window.matchMedia('(prefers-reduced-motion: reduce)').matches
      || /jsdom/i.test(navigator.userAgent)
      // A page the reader came back to is simply there (animated-outlet.tsx, RouteFrame).
      || !!el.closest('[data-motion="settled"]')
    if (reduce || from === value || Math.abs(value) < 2) { displayed.current = value; return }
    const [x1, y1, x2, y2] = EASE.emphasized
    // Cubic-bezier solved for t by a few Newton steps: the same curve the CSS uses.
    const bez = (p: number) => {
      let t = p
      for (let i = 0; i < 5; i++) {
        const x = 3 * (1 - t) ** 2 * t * x1 + 3 * (1 - t) * t ** 2 * x2 + t ** 3 - p
        const dx = 3 * (1 - t) ** 2 * x1 + 6 * (1 - t) * t * (x2 - x1) + 3 * t ** 2 * (1 - x2)
        if (Math.abs(dx) < 1e-6) break
        t -= x / dx
      }
      return 3 * (1 - t) ** 2 * t * y1 + 3 * (1 - t) * t ** 2 * y2 + t ** 3
    }
    // Only a figure that is one text node is counted; anything richer is simply shown.
    const node = el.childNodes.length === 1 && el.firstChild?.nodeType === Node.TEXT_NODE ? el.firstChild : null
    if (!node) { displayed.current = value; return }
    // Interrupted mid-count with no new text from React: the node still holds our number, so put
    // React's text back before reading it.
    if (written.current && node.nodeValue === written.current.shown) node.nodeValue = written.current.final
    const final = node.nodeValue
    const start = performance.now()
    const ms = DURATION.count * 1000
    let raf = 0
    const tick = (now: number) => {
      const p = Math.max(0, Math.min(1, (now - start) / ms))
      const n = p >= 1 ? value : Math.round(from + (value - from) * bez(p))
      displayed.current = n
      node.nodeValue = p >= 1 ? final : String(n)
      written.current = p >= 1 ? null : { shown: String(n), final }
      if (p < 1) raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(raf)
  }, [value])
  return ref
}
